import { useEffect, useState } from "react";
import { cn } from "@vseh/ui";

/**
 * Эффект «печатной машинки» для стриминга ответа от LLM.
 *
 * Принцип: получает целевой текст и постепенно «печатает» его буква за
 * буквой. Курсор `▌` пульсирует пока не завершено. После окончания —
 * курсор пропадает, текст остаётся.
 *
 * Когда контент по факту приходит чанками от LLM API — оборачивай весь
 * накопленный текст в этот компонент и обновляй prop `text` по мере
 * прихода новых чанков.
 */
export interface StreamingTextProps {
  /** Целевой текст. По мере роста — компонент дотягивает анимацию. */
  text: string;
  /** Скорость печати в мс/символ. По умолчанию 12 мс. */
  charDelayMs?: number;
  /** Когда true, считаем стрим завершённым — курсор скрывается. */
  done?: boolean;
  className?: string;
}

export function StreamingText({
  text,
  charDelayMs = 12,
  done = false,
  className,
}: StreamingTextProps): JSX.Element {
  const [shown, setShown] = useState("");

  useEffect(() => {
    if (shown.length >= text.length) {
      return;
    }
    const next = text.slice(0, shown.length + 1);
    const timer = window.setTimeout(() => setShown(next), charDelayMs);
    return () => window.clearTimeout(timer);
  }, [shown, text, charDelayMs]);

  const showCursor = !done || shown.length < text.length;

  return (
    <span className={cn("whitespace-pre-wrap text-md text-base-900", className)}>
      {shown}
      {showCursor && (
        <span
          aria-hidden="true"
          className="ml-0.5 inline-block w-[1ch] animate-pulse text-ai-500"
        >
          ▌
        </span>
      )}
    </span>
  );
}
